var __jsx = React.createElement;
import React from "react";
import AwesomeIcon from "../../AwesomeIcon";
import { faAsterisk, faEye } from "@fortawesome/pro-solid-svg-icons";
import FieldInputRenderer from "./FieldInputRenderer";
import Tooltip from "../../common/Tooltip";
import classNames from "classnames";
import { isEmptyValue, isConditionalRequiredError } from "../../../utils/formValueUtils";
import { useTranslation } from "react-i18next";
var FormField = function FormField(_ref) {
  var _conditionalError$con, _field$metadata, _field$metadata$valid, _field$metadata2, _conditionalError$con2, _conditionalError$con3, _field$metadata4;
  var field = _ref.field,
    status = _ref.status,
    isFieldRequiredProp = _ref.isFieldRequired,
    formData = _ref.formData,
    handleFieldChange = _ref.handleFieldChange,
    onDropdownStateChange = _ref.onDropdownStateChange,
    serverError = _ref.serverError,
    validationError = _ref.validationError,
    conditionalError = _ref.conditionalError,
    _ref$isSubmissionRead = _ref.isSubmissionReadOnly,
    isSubmissionReadOnly = _ref$isSubmissionRead === void 0 ? false : _ref$isSubmissionRead,
    fieldDataSource = _ref.fieldDataSource,
    highlightedFieldName = _ref.highlightedFieldName,
    _ref$isServerMissing = _ref.isServerMissing,
    isServerMissing = _ref$isServerMissing === void 0 ? false : _ref$isServerMissing,
    labelRef = _ref.labelRef,
    _ref$labelMarginBotto = _ref.labelMarginBottom,
    labelMarginBottom = _ref$labelMarginBotto === void 0 ? 0 : _ref$labelMarginBotto;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;

  // Show validation error or server error
  var errorMessage = validationError || serverError;
  var isDisabled = field.source === "auto" || field.is_read_only || isSubmissionReadOnly;

  // Determine error type and styling for red asterisk display
  var isConditionalRequired = isConditionalRequiredError(conditionalError);
  var isSpecialCheckRequired = (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.type) === "special_check" && (conditionalError === null || conditionalError === void 0 || (_conditionalError$con = conditionalError.conditional_metadata) === null || _conditionalError$con === void 0 ? void 0 : _conditionalError$con.type) === "required";

  // Check if field is empty
  var fieldValue = formData[field.field_name];
  var isEmpty = isEmptyValue(fieldValue);

  // Use the required status passed from parent (calculated centrally in GroupedFormSection)
  // This consolidates all required checks: static, conditional, special_check, server-missing
  var isRequiredField = isFieldRequiredProp;

  // Check for static required (needed for asterisk display)
  var isStaticRequired = field.is_acr_required || field.visibility === "required";

  // Check if field is an LLM-filled field using per-submission source (for gradient border)
  var actualSource = fieldDataSource;
  var isLLMField = actualSource === "emr_llm" || actualSource === "llm";

  // Check if this field is highlighted (for jump to next feature)
  var isHighlighted = highlightedFieldName === field.field_name;

  // Check if field is a badge multi-select (should span full width)
  var isBadgeMultiSelect = ((_field$metadata = field.metadata) === null || _field$metadata === void 0 ? void 0 : _field$metadata.multi_select) && ((_field$metadata$valid = (_field$metadata2 = field.metadata) === null || _field$metadata2 === void 0 || (_field$metadata2 = _field$metadata2.valid_values) === null || _field$metadata2 === void 0 ? void 0 : _field$metadata2.length) !== null && _field$metadata$valid !== void 0 ? _field$metadata$valid : 0) > 0;

  // Check if this is a persistent error that should always be shown
  // (should_be_blank conditional errors and required special_check errors)
  var isShouldBeBlankError = (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.type) === "conditional" && (conditionalError === null || conditionalError === void 0 || (_conditionalError$con2 = conditionalError.conditional_metadata) === null || _conditionalError$con2 === void 0 ? void 0 : _conditionalError$con2.type) === "should_be_blank" || (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.type) === "special_check" && (conditionalError === null || conditionalError === void 0 || (_conditionalError$con3 = conditionalError.conditional_metadata) === null || _conditionalError$con3 === void 0 ? void 0 : _conditionalError$con3.type) !== "optional";

  // Check if field has conditional requirement for tooltip
  var hasConditionalRequirement = (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.type) === "conditional" && (conditionalError === null || conditionalError === void 0 ? void 0 : conditionalError.conditional_metadata) && (conditionalError.conditional_metadata.type === "required" || conditionalError.conditional_metadata.type === "optional");

  // Get conditional tooltip message directly from field schema's conditional validations
  var conditionalTooltipMessage = React.useMemo(function () {
    var _field$metadata3;
    if (!hasConditionalRequirement || !(conditionalError !== null && conditionalError !== void 0 && conditionalError.conditional_metadata)) {
      return null;
    }
    var parentField = conditionalError.conditional_metadata.field;
    var conditionalValidations = (_field$metadata3 = field.metadata) === null || _field$metadata3 === void 0 ? void 0 : _field$metadata3.conditional_validations;
    if (!conditionalValidations || conditionalValidations.length === 0) {
      return null;
    }

    // Find the matching rule based on the parent field
    var matchingRule = conditionalValidations.find(function (rule) {
      return rule.depends_on === parentField;
    });

    // Return the exact message from the rule
    return (matchingRule === null || matchingRule === void 0 ? void 0 : matchingRule.message) || null;
  }, [hasConditionalRequirement, conditionalError, (_field$metadata4 = field.metadata) === null || _field$metadata4 === void 0 ? void 0 : _field$metadata4.conditional_validations]);
  return /*#__PURE__*/React.createElement("div", {
    "data-field-name": field.field_name,
    className: classNames("flex flex-col h-full w-full group rounded-lg transition-all", {
      "opacity-95": isSubmissionReadOnly,
      "border-2 border-teal-500 dark:border-teal-400": isHighlighted,
      "border-2 border-transparent": !isHighlighted,
      "md:col-span-2": isBadgeMultiSelect
    })
  }, /*#__PURE__*/React.createElement("div", {
    ref: labelRef,
    className: "flex gap-2 items-start text-left text-md min-h-[18px]",
    style: {
      marginBottom: labelMarginBottom > 0 ? "".concat(labelMarginBottom, "px") : undefined
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "ml-2 flex-1 text-gray-700 dark:text-gray-300"
  }, field.label, (isServerMissing || isStaticRequired || isConditionalRequired || isSpecialCheckRequired) && /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faAsterisk,
    className: "h-3 p-2 relative -top-2 text-red-500"
  }), isDisabled && /*#__PURE__*/React.createElement("span", {
    className: "ml-2 text-xs text-gray-500 dark:text-gray-500 italic"
  }, "(read-only)")), hasConditionalRequirement && /*#__PURE__*/React.createElement(Tooltip, {
    tooltipText: conditionalTooltipMessage,
    position: "right",
    tooltipClass: "!bg-slate-900 dark:!bg-black !px-4 !py-3 !text-gray-100",
    className: "inline-block [&_.tooltip-trigger]:m-0 [&_.tooltip-trigger]:ml-2 [&_.tooltip-content]:!min-w-0 [&_.tooltip-content]:w-64 [&_.tooltip-content]:whitespace-normal [&_.tooltip-content]:break-words"
  }, /*#__PURE__*/React.createElement("span", {
    className: "inline-flex gap-2 items-center pl-2 pr-3 rounded-12 text-sm font-medium bg-teal-200/20 text-teal-800 dark:bg-teal-300/20 dark:text-teal-300 border border-teal-400 cursor-help tracking-wide"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faEye,
    className: "h-3 w-4"
  }), t("label.condition"))), isLLMField && /*#__PURE__*/React.createElement("img", {
    src: "/assets/icons/aira-logo.svg",
    alt: "AI filled",
    className: "h-5 w-5 mr-2 flex-shrink-0 object-contain"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex gap-3 items-center mt-2"
  }, /*#__PURE__*/React.createElement(FieldInputRenderer, {
    field: field,
    value: formData[field.field_name],
    onChange: function onChange(value) {
      return handleFieldChange(field.field_name, value);
    },
    onDropdownStateChange: onDropdownStateChange,
    disabled: isDisabled,
    isEmpty: isEmpty,
    isRequired: isRequiredField,
    errorMessage: errorMessage,
    isShouldBeBlankError: isShouldBeBlankError
  })));
};
export default FormField;